import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { LogOut, ChevronsUpDown, UserCog, type LucideIcon } from "lucide-react";
import { Badge } from "./badge";

/**
 * Shared portal sidebar shell (design-system-spec §2.7): fixed w-56 column on
 * the dark chrome surface — brand block, scrollable nav (children), pinned
 * footer. Each portal supplies its own nav model and footer widgets.
 */
export function SidebarShell({ title, subtitle, footer, children }: {
  title: string;
  subtitle: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <aside className="flex h-screen w-56 shrink-0 flex-col border-r border-white/10 bg-[var(--color-header)]">
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-white/10 px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded bg-primary text-xs font-bold text-primary-foreground">
          PGS
        </div>
        <div>
          <p className="text-sm font-semibold leading-none text-white">{title}</p>
          <p className="text-xs text-white/60">{subtitle}</p>
        </div>
      </div>

      <nav aria-label="Primary" className="flex-1 overflow-y-auto p-2">
        {children}
      </nav>

      {footer && <div className="shrink-0 border-t border-white/10 p-2">{footer}</div>}
    </aside>
  );
}

export function SidebarNavItem({ href, icon: Icon, label, exact, badge = 0 }: {
  href: string;
  icon: LucideIcon;
  label: string;
  exact?: boolean;
  /** Optional count chip (e.g. unread submissions), hidden at 0. */
  badge?: number;
}) {
  return (
    <NavLink
      to={href}
      end={exact}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-white/10 text-white"
            : "text-white/60 hover:bg-white/5 hover:text-white/90"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <Badge variant="default" className="h-5 min-w-5 justify-center px-1.5 text-[10px]">
          {badge}
        </Badge>
      )}
    </NavLink>
  );
}

/** Uppercase group heading between nav sections. */
export function SidebarGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-white/30">
      {children}
    </p>
  );
}

// Environment the portal's API is connected to (dev:local / dev:remote / prod).
const ENV_DOT: Record<string, string> = {
  "dev:local": "bg-success",
  "dev:remote": "bg-info",
  prod: "bg-warning",
};

export function SidebarEnvIndicator({ environment }: { environment?: string }) {
  if (!environment) return null;
  const dot = ENV_DOT[environment] || "bg-white/40";
  return (
    <div
      className="mb-1 flex items-center gap-2 rounded-md bg-white/5 px-2.5 py-1.5"
      title={`Connected to ${environment}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
        Env
      </span>
      <span className="ml-auto truncate font-mono text-xs font-medium text-white/90">
        {environment}
      </span>
    </div>
  );
}

/** Signed-in user row with a pop-up menu (account + sign out), pinned in the footer. */
export function SidebarUserMenu({ email, accountHref, onSignOut }: {
  email: string;
  /** When set, the menu shows a link to the account/profile page. */
  accountHref?: string;
  onSignOut?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initial = (email[0] || "?").toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1 overflow-hidden rounded-md border border-white/10 bg-[var(--color-header)] py-1 shadow-lg">
          {accountHref && (
            <NavLink
              to={accountHref}
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            >
              <UserCog className="h-4 w-4" />
              My account
            </NavLink>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut?.();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left transition-colors hover:bg-white/5"
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {initial}
        </div>
        <span className="min-w-0 flex-1 truncate text-sm text-white/80">{email}</span>
        <ChevronsUpDown className="h-4 w-4 shrink-0 text-white/40" />
      </button>
    </div>
  );
}
